import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import usersService, { Department } from '../../services/usersService';
import timesheetCategoriesService from '../../services/timesheetCategoriesService';
import {
  TimesheetCustomCategory,
  TimesheetCustomSubcategory,
  User,
} from '../../types';
import LoadingAnimation from '../LoadingAnimation';
import { getZoomCompensatedAutocompleteSlotProps } from '../../utils/zoomCompensation';
import { useFormFieldErrors } from '../../hooks/useFormFieldErrors';
import FormValidationAlert from '../FormValidationAlert';

interface Sub2Form {
  id?: number;
  name: string;
}

interface SubForm {
  id?: number;
  name: string;
  subcategories2: Sub2Form[];
}

interface CategoryFormState {
  name: string;
  is_active: boolean;
  subcategories: SubForm[];
  user_ids: number[];
  department_ids: number[];
}

const emptyForm = (): CategoryFormState => ({
  name: '',
  is_active: true,
  subcategories: [],
  user_ids: [],
  department_ids: [],
});

const CustomCategoriesTab: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b' };
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [categories, setCategories] = useState<TimesheetCustomCategory[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TimesheetCustomCategory | null>(null);
  const [form, setForm] = useState<CategoryFormState>(emptyForm());

  const [deleteTarget, setDeleteTarget] = useState<TimesheetCustomCategory | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fieldErrors = useFormFieldErrors();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [cats, usersData, deptsData] = await Promise.all([
        timesheetCategoriesService.list(),
        usersService.getUsers(),
        usersService.getDepartments(),
      ]);
      setCategories(cats);
      setUsers(usersData);
      setDepartments(deptsData);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load custom categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    fieldErrors.clear();
    setDialogOpen(true);
  };

  const openEdit = (cat: TimesheetCustomCategory) => {
    setEditing(cat);
    setForm({
      name: cat.name,
      is_active: cat.is_active,
      subcategories: (cat.subcategories || []).map((s: TimesheetCustomSubcategory) => ({
        id: s.id,
        name: s.name,
        subcategories2: (s.subcategories2 || []).map((s2) => ({
          id: s2.id,
          name: s2.name,
        })),
      })),
      user_ids: cat.user_ids || (cat.users || []).map((u) => u.id),
      department_ids: cat.department_ids || (cat.departments || []).map((d) => d.id),
    });
    fieldErrors.clear();
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) {
      fieldErrors.show('Category name is required', ['name']);
      return;
    }

    const payload = {
      name,
      is_active: form.is_active,
      user_ids: form.user_ids,
      department_ids: form.department_ids,
      subcategories: form.subcategories
        .filter((s) => s.name.trim())
        .map((s) => ({
          id: s.id,
          name: s.name.trim(),
          subcategories2: s.subcategories2
            .filter((s2) => s2.name.trim())
            .map((s2) => ({ id: s2.id, name: s2.name.trim() })),
        })),
    };

    try {
      setSaving(true);
      fieldErrors.clear();
      if (editing) {
        const result = await timesheetCategoriesService.update(editing.id, payload);
        if (result.warnings?.length) {
          setSuccess(`Category updated (with warnings: ${result.warnings.join('; ')})`);
        } else {
          setSuccess('Category updated');
        }
      } else {
        await timesheetCategoriesService.create(payload);
        setSuccess('Category created');
      }
      setDialogOpen(false);
      setTimeout(() => setSuccess(null), 5000);
      await load();
    } catch (err: any) {
      fieldErrors.show(err.response?.data?.error || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (cat: TimesheetCustomCategory) => {
    try {
      setError(null);
      await timesheetCategoriesService.update(cat.id, {
        name: cat.name,
        is_active: !cat.is_active,
      });
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update category');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      setError(null);
      await timesheetCategoriesService.remove(deleteTarget.id);
      setDeleteTarget(null);
      setSuccess('Category deleted');
      setTimeout(() => setSuccess(null), 5000);
      await load();
    } catch (err: any) {
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.suggest_deactivate) {
        setError(data.error || 'Category has logged hours — deactivate instead');
        setDeleteTarget(null);
      } else {
        setError(data?.error || 'Failed to delete category');
      }
    } finally {
      setDeleting(false);
    }
  };

  const addSubcategory = () => {
    setForm((prev) => ({
      ...prev,
      subcategories: [...prev.subcategories, { name: '', subcategories2: [] }],
    }));
  };

  const updateSubcategory = (index: number, name: string) => {
    setForm((prev) => {
      const next = [...prev.subcategories];
      next[index] = { ...next[index], name };
      return { ...prev, subcategories: next };
    });
  };

  const removeSubcategory = (index: number) => {
    setForm((prev) => ({
      ...prev,
      subcategories: prev.subcategories.filter((_, i) => i !== index),
    }));
  };

  const addSubcategory2 = (subIndex: number) => {
    setForm((prev) => {
      const next = [...prev.subcategories];
      next[subIndex] = {
        ...next[subIndex],
        subcategories2: [...next[subIndex].subcategories2, { name: '' }],
      };
      return { ...prev, subcategories: next };
    });
  };

  const updateSubcategory2 = (subIndex: number, s2Index: number, name: string) => {
    setForm((prev) => {
      const next = [...prev.subcategories];
      const s2 = [...next[subIndex].subcategories2];
      s2[s2Index] = { ...s2[s2Index], name };
      next[subIndex] = { ...next[subIndex], subcategories2: s2 };
      return { ...prev, subcategories: next };
    });
  };

  const removeSubcategory2 = (subIndex: number, s2Index: number) => {
    setForm((prev) => {
      const next = [...prev.subcategories];
      next[subIndex] = {
        ...next[subIndex],
        subcategories2: next[subIndex].subcategories2.filter((_, i) => i !== s2Index),
      };
      return { ...prev, subcategories: next };
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <LoadingAnimation />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontSize: '0.7rem',
            color: isDark ? '#b0bec5' : '#546e7a',
          }}
        >
          Custom Categories
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{
            borderRadius: 1,
            boxShadow: 'none',
            bgcolor: NAUTILUS.sand,
            color: NAUTILUS.deepOcean,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontWeight: 600,
            fontSize: '0.75rem',
            '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' },
          }}
        >
          New Category
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        sx={{ borderRadius: 1, boxShadow: 'none', border: `1px solid ${panelBorder}`, bgcolor: surfaceBg }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Subcategories</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Access</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">
                Active
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">
                Entries
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No custom categories yet. Create one to let users log non-project time.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              categories.map((cat) => (
                <TableRow key={cat.id}>
                  <TableCell>
                    <Typography fontWeight={600}>{cat.name}</Typography>
                  </TableCell>
                  <TableCell>
                    {(cat.subcategories || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    ) : (
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {cat.subcategories.map((s) => (
                          <Chip
                            key={s.id || s.name}
                            size="small"
                            label={
                              s.subcategories2?.length
                                ? `${s.name} (${s.subcategories2.map((x) => x.name).join(', ')})`
                                : s.name
                            }
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {(cat.users || []).map((u) => (
                        <Chip key={`u-${u.id}`} size="small" label={u.name} color="primary" variant="outlined" />
                      ))}
                      {(cat.departments || []).map((d) => (
                        <Chip
                          key={`d-${d.id}`}
                          size="small"
                          label={d.department}
                          color="secondary"
                          variant="outlined"
                        />
                      ))}
                      {(cat.users || []).length === 0 && (cat.departments || []).length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          No access granted
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={cat.is_active}
                      onChange={() => handleToggleActive(cat)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="center">{cat.entry_count ?? 0}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(cat)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(cat)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); fieldErrors.clear(); }} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Custom Category' : 'New Custom Category'}</DialogTitle>
        <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            required
            label="Category name"
            value={form.name}
            error={fieldErrors.invalid('name')}
            onChange={(e) => {
              fieldErrors.clearField('name');
              setForm((prev) => ({ ...prev, name: e.target.value }));
            }}
            margin="normal"
            helperText="Mandatory. Maps to the Project column in the timesheet grid."
          />

          {editing && (
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                />
              }
              label="Active"
              sx={{ mt: 1, mb: 1 }}
            />
          )}

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Subcategories (optional)
          </Typography>
          {form.subcategories.map((sub, subIndex) => (
            <Paper
              key={subIndex}
              variant="outlined"
              sx={{ p: 2, mb: 1.5, borderColor: panelBorder }}
            >
              <Box display="flex" gap={1} alignItems="center" mb={1}>
                <TextField
                  fullWidth
                  size="small"
                  label="Subcategory"
                  value={sub.name}
                  onChange={(e) => updateSubcategory(subIndex, e.target.value)}
                  helperText="Maps to Work Package"
                />
                <IconButton color="error" onClick={() => removeSubcategory(subIndex)}>
                  <DeleteIcon />
                </IconButton>
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Subcategory 2 (optional — maps to Work Unit)
              </Typography>
              {sub.subcategories2.map((s2, s2Index) => (
                <Box key={s2Index} display="flex" gap={1} alignItems="center" mb={1} pl={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Subcategory 2"
                    value={s2.name}
                    disabled={!sub.name.trim()}
                    onChange={(e) => updateSubcategory2(subIndex, s2Index, e.target.value)}
                  />
                  <IconButton color="error" onClick={() => removeSubcategory2(subIndex, s2Index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                disabled={!sub.name.trim()}
                onClick={() => addSubcategory2(subIndex)}
              >
                Add Subcategory 2
              </Button>
            </Paper>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addSubcategory} sx={{ mb: 2 }}>
            Add Subcategory
          </Button>

          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
            Access
          </Typography>
          <Autocomplete
            multiple
            options={users}
            getOptionLabel={(u) => u.name}
            value={users.filter((u) => form.user_ids.includes(u.id))}
            onChange={(_, selected) =>
              setForm((prev) => ({ ...prev, user_ids: selected.map((u) => u.id) }))
            }
            slotProps={getZoomCompensatedAutocompleteSlotProps()}
            renderInput={(params) => (
              <TextField {...params} label="Users" margin="dense" helperText="Users who can log time on this category" />
            )}
          />
          <Autocomplete
            multiple
            options={departments}
            getOptionLabel={(d) => d.department}
            value={departments.filter((d) => form.department_ids.includes(d.id))}
            onChange={(_, selected) =>
              setForm((prev) => ({ ...prev, department_ids: selected.map((d) => d.id) }))
            }
            slotProps={getZoomCompensatedAutocompleteSlotProps()}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Departments"
                margin="dense"
                helperText="All users in these departments can log time on this category"
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); fieldErrors.clear(); }} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              boxShadow: 'none',
              bgcolor: NAUTILUS.sand,
              color: NAUTILUS.deepOcean,
              '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' },
            }}
          >
            {saving ? 'Saving...' : editing ? 'Save changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete category?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone. If hours have already been logged,
            deletion will be blocked — deactivate the category instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomCategoriesTab;
